<template>
  <section class="bg-white/80 py-18">
    <div class="container mx-auto px-4">
      <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-6" v-scroll-reveal="{ delay: 0.1 }">
        <div>
          <p class="text-sm uppercase tracking-[0.7em] text-amber-500">Tailored Services</p>
          <h2 class="mt-3 text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl">
            Book Individual Services with Confidence
          </h2>
          <p class="mt-3 text-gray-600 max-w-2xl">
            Choose only the parts you need—luxury hotels, visa assistance, premium transport, or global flight deals.
            Each request is handled by our dedicated Umrah concierge.
          </p>
        </div>
        <router-link
          to="/contact"
          class="inline-flex items-center gap-2 rounded-full border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100"
        >
          Speak to a Planner
          <svg class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </router-link>
      </div>

      <div class="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
        <router-link
          v-for="service, index in specialtyServices"
          :key="service.title"
          :to="service.link"
          class="group relative flex h-[320px] cursor-pointer flex-col overflow-hidden rounded-[28px] border border-gray-100 bg-white p-8 shadow-sm transition duration-300 ease-out hover:-translate-y-2 hover:border-transparent hover:bg-amber-50"
          v-scroll-reveal="{ delay: index * 0.12 }"
        >
          <!-- Header: Badge and Icon -->
          <div class="flex flex-shrink-0 items-center justify-between">
            <span class="rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-amber-600">
              {{ service.badge }}
            </span>
            <div class="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 transition group-hover:bg-amber-100 group-hover:text-amber-700">
              <svg class="h-6 w-6" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" :d="service.icon" />
              </svg>
            </div>
          </div>
          
          <!-- Title -->
          <h3 class="mt-6 flex-shrink-0 text-2xl font-semibold text-gray-900 transition group-hover:text-amber-600">{{ service.title }}</h3>
          
          <!-- Description: Fixed height with truncation -->
          <div class="mt-4 flex-1 flex items-start">
            <p class="text-sm leading-relaxed text-gray-600 line-clamp-3">{{ truncateText(service.description, 100) }}</p>
          </div>
          
          <!-- CTA: Sticky footer -->
          <div class="mt-8 flex-shrink-0 flex items-center text-sm font-semibold text-amber-600 group-hover:text-amber-700">
            {{ service.cta }}
            <svg class="ml-2 h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14M13 5l7 7-7 7" />
            </svg>
          </div>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'SpecialtyServices',
  data() {
    return {
      specialtyServices: [
        {
          title: 'Hotel Only Booking',
          badge: 'Hotel',
          description: 'Premium Makkah & Madina rooms adjacent to Haram with breakfast add-ons and early check-in support.',
          cta: 'View Hotel Inventory',
          link: '/hotels',
          icon: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25'
        },
        {
          title: 'Flights & Visa Desk',
          badge: 'Flights',
          description: 'Any-country departures with low fares, coordinated with fast-track Umrah visa processing.',
          cta: 'Get Flight Deals',
          link: '/flights',
          icon: 'M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.874L5.999 12zm0 0h7.5'
        },
        {
          title: 'Private Transport',
          badge: 'Transport',
          description: 'Chauffeur-driven luxury vans for Jeddah airport transfers, Ziyarat tours, and intercity trips.',
          cta: 'Reserve Transport',
          link: '/contact',
          icon: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12'
        },
        {
          title: 'Visa Assistance ',
          badge: 'Visa',
          description: 'Dedicated case officers handle biometrics, documentation, and updates for Umrah visas worldwide.',
          cta: 'Start Visa Process',
          link: '/contact',
          icon: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z'
        }
      ]
    }
  },
  methods: {
    truncateText(text, maxLength) {
      if (text.length <= maxLength) {
        return text
      }
      return text.substring(0, maxLength) + '...'
    }
  }
}
</script>

<style scoped>
.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

